// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The imported model as layers (frontend-ayw.7). `rux import speckle` and
 * `rux import ifc` store one mesh per category, named
 * `imported/<source>/<slug>` (`imported/revit/walls`,
 * `imported/ifc/furnishing_element`, …); every other mesh is the scan's own
 * (`mesh`, from `rux create mesh`). This module splits `GET /meshes` into
 * the scan mesh and the model's categories, names the categories in Danish
 * (falling back to the slug), orders them as a plan reads (structure,
 * openings, circulation, then the fittings), says which are drawn by
 * default — in the locked plan view the fittings, ceilings and roofs are
 * off, they hide the plan — and which colour token each kind is drawn in.
 * Pure: no React, no DOM.
 */

import type { MeshInfo } from '../api/types';

/** What a category is, for its order, default visibility and colour. */
export type ModelKind =
  | 'wall'
  | 'slab'
  | 'column'
  | 'door'
  | 'window'
  | 'circulation'
  | 'ceiling'
  | 'roof'
  | 'interior'
  | 'services'
  | 'space'
  | 'other';

/** One imported category mesh. */
export interface ModelLayer {
  /** The mesh's name, `imported/<source>/<category>` — the scene layer id and the settings key. */
  name: string;
  /** `revit`, `ifc`, … (the second path segment). */
  source: string;
  /** The category slug (the rest of the name). */
  category: string;
  /** Danish name for the toggle. */
  label: string;
  kind: ModelKind;
  info: MeshInfo;
}

/** The imported categories of one source, in plan order. */
export interface ModelGroup {
  source: string;
  /** `Revit`, `IFC`, else the slug. */
  sourceLabel: string;
  layers: ModelLayer[];
}

/** `GET /meshes` split: the scan's mesh (the first that is not imported) and the model by source. */
export interface MeshSplit {
  scan: MeshInfo | null;
  model: ModelGroup[];
}

const PREFIX = 'imported/';

/** `{ source, category }` of an imported mesh name, or null for any other mesh. */
export function parseImportedMesh(name: string): { source: string; category: string } | null {
  if (!name.startsWith(PREFIX)) return null;
  const rest = name.slice(PREFIX.length);
  const slash = rest.indexOf('/');
  if (slash <= 0 || slash === rest.length - 1) return null;
  return { source: rest.slice(0, slash), category: rest.slice(slash + 1) };
}

/** Revit category slugs (`mesh_slug` of the category name) and IFC entity slugs → Danish name and kind. */
const CATEGORIES: Record<string, { label: string; kind: ModelKind }> = {
  // Revit
  walls: { label: 'Vægge', kind: 'wall' },
  curtain_panels: { label: 'Facadepaneler', kind: 'wall' },
  curtain_wall_mullions: { label: 'Facadeprofiler', kind: 'wall' },
  floors: { label: 'Dæk', kind: 'slab' },
  structural_foundations: { label: 'Fundamenter', kind: 'slab' },
  columns: { label: 'Søjler', kind: 'column' },
  structural_columns: { label: 'Bærende søjler', kind: 'column' },
  structural_framing: { label: 'Bjælker', kind: 'column' },
  doors: { label: 'Døre', kind: 'door' },
  windows: { label: 'Vinduer', kind: 'window' },
  stairs: { label: 'Trapper', kind: 'circulation' },
  railings: { label: 'Gelændere', kind: 'circulation' },
  ramps: { label: 'Ramper', kind: 'circulation' },
  ceilings: { label: 'Lofter', kind: 'ceiling' },
  roofs: { label: 'Tage', kind: 'roof' },
  furniture: { label: 'Inventar', kind: 'interior' },
  furniture_systems: { label: 'Inventarsystemer', kind: 'interior' },
  casework: { label: 'Faste skabe', kind: 'interior' },
  specialty_equipment: { label: 'Specialudstyr', kind: 'interior' },
  plumbing_fixtures: { label: 'Sanitet', kind: 'services' },
  lighting_fixtures: { label: 'Belysning', kind: 'services' },
  electrical_fixtures: { label: 'El-installationer', kind: 'services' },
  electrical_equipment: { label: 'El-udstyr', kind: 'services' },
  mechanical_equipment: { label: 'Tekniske anlæg', kind: 'services' },
  pipes: { label: 'Rør', kind: 'services' },
  ducts: { label: 'Kanaler', kind: 'services' },
  generic_models: { label: 'Generiske elementer', kind: 'other' },
  topography: { label: 'Terræn', kind: 'other' },
  site: { label: 'Grund', kind: 'other' },
  // IFC
  wall: { label: 'Vægge', kind: 'wall' },
  wall_standard_case: { label: 'Vægge', kind: 'wall' },
  curtain_wall: { label: 'Glasfacader', kind: 'wall' },
  slab: { label: 'Dæk', kind: 'slab' },
  footing: { label: 'Fundamenter', kind: 'slab' },
  column: { label: 'Søjler', kind: 'column' },
  beam: { label: 'Bjælker', kind: 'column' },
  member: { label: 'Profiler', kind: 'column' },
  plate: { label: 'Plader', kind: 'wall' },
  door: { label: 'Døre', kind: 'door' },
  window: { label: 'Vinduer', kind: 'window' },
  stair: { label: 'Trapper', kind: 'circulation' },
  stair_flight: { label: 'Trappeløb', kind: 'circulation' },
  railing: { label: 'Gelændere', kind: 'circulation' },
  ramp: { label: 'Ramper', kind: 'circulation' },
  covering: { label: 'Beklædning', kind: 'ceiling' },
  roof: { label: 'Tage', kind: 'roof' },
  furnishing_element: { label: 'Inventar', kind: 'interior' },
  flow_terminal: { label: 'Sanitet og armaturer', kind: 'services' },
  building_element_proxy: { label: 'Øvrige elementer', kind: 'other' },
  space: { label: 'Rum', kind: 'space' },
};

/** Plan order of the kinds: what a plan is read by first. */
const KIND_ORDER: readonly ModelKind[] = [
  'wall',
  'slab',
  'column',
  'door',
  'window',
  'circulation',
  'ceiling',
  'roof',
  'interior',
  'services',
  'other',
  'space',
];

/** The category's Danish name; an unknown slug reads as itself (`generic_thing` → `Generic thing`). */
export function categoryLabel(category: string): string {
  const known = CATEGORIES[category];
  if (known) return known.label;
  const words = category.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : category;
}

/** The category's kind; `other` when unknown. */
export function categoryKind(category: string): ModelKind {
  return CATEGORIES[category]?.kind ?? 'other';
}

/** `Revit`, `IFC`, else the slug. */
export function sourceLabel(source: string): string {
  if (source === 'revit') return 'Revit';
  if (source === 'ifc') return 'IFC';
  return source;
}

/** Hidden by default in the locked plan: what stands on or hangs over the floor plan. */
const PLAN_HIDDEN: ReadonlySet<ModelKind> = new Set(['interior', 'services', 'ceiling', 'roof', 'space']);
/** Hidden by default everywhere: IFC spaces are volumes that would wrap every room. */
const ALWAYS_HIDDEN: ReadonlySet<ModelKind> = new Set(['space']);

/** Whether a category is drawn when the user has not switched it. */
export function defaultLayerVisible(kind: ModelKind, plan: boolean): boolean {
  return plan ? !PLAN_HIDDEN.has(kind) : !ALWAYS_HIDDEN.has(kind);
}

/** Whether a layer is drawn: the user's own switch, else the default for the view. */
export function modelLayerOn(switches: Readonly<Record<string, boolean>>, layer: Pick<ModelLayer, 'name' | 'kind'>, plan: boolean): boolean {
  return switches[layer.name] ?? defaultLayerVisible(layer.kind, plan);
}

/**
 * The colour token a kind is drawn in. Walls stand out from the slabs they
 * stand on (from above, both are flat-lit tops), openings take two of the
 * categorical label colours, the rest stays neutral.
 */
export function kindColorToken(kind: ModelKind): string {
  switch (kind) {
    case 'wall':
    case 'column':
      return '--mesh-surface';
    case 'slab':
      return '--color-border-strong';
    case 'door':
      return '--label-0';
    case 'window':
      return '--label-1';
    case 'circulation':
      return '--color-text-muted';
    case 'interior':
    case 'services':
      return '--label-7';
    default:
      return '--mesh-surface';
  }
}

/**
 * The solids stage's per-type meshes (`solid_walls`, `solid_slabs`,
 * `solid_spaces`, …): the building elements, which the Ground draws one by
 * one from `GET /elements` — never the scan's own mesh (frontend-r22.27).
 */
export function isSolidsMesh(name: string): boolean {
  return name.startsWith('solid_');
}

/**
 * Split `GET /meshes` into the scan's mesh and the imported model, grouped
 * by source in plan order. The solids stage's meshes are neither.
 */
export function splitMeshes(meshes: readonly MeshInfo[] | null | undefined): MeshSplit {
  let scan: MeshInfo | null = null;
  const bySource = new Map<string, ModelLayer[]>();
  for (const info of meshes ?? []) {
    if (isSolidsMesh(info.name)) continue;
    const parsed = parseImportedMesh(info.name);
    if (!parsed) {
      scan ??= info;
      continue;
    }
    const layer: ModelLayer = {
      name: info.name,
      source: parsed.source,
      category: parsed.category,
      label: categoryLabel(parsed.category),
      kind: categoryKind(parsed.category),
      info,
    };
    const list = bySource.get(parsed.source);
    if (list) list.push(layer);
    else bySource.set(parsed.source, [layer]);
  }
  const model: ModelGroup[] = [...bySource]
    .sort(([a], [b]) => sourceRank(a) - sourceRank(b) || a.localeCompare(b))
    .map(([source, layers]) => ({
      source,
      sourceLabel: sourceLabel(source),
      layers: layers.sort(
        (a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || a.label.localeCompare(b.label, 'da') || a.name.localeCompare(b.name),
      ),
    }));
  return { scan, model };
}

function sourceRank(source: string): number {
  return source === 'revit' ? 0 : source === 'ifc' ? 1 : 2;
}

/** Every imported layer, flat, in group order. */
export function modelLayers(split: MeshSplit): ModelLayer[] {
  return split.model.flatMap((g) => g.layers);
}

/** One model layer's download: in flight, on screen, or failed. */
export type LayerLoad = 'loading' | 'loaded' | 'error';

/**
 * What the imported model as a whole is doing, for the Ground's status line:
 * `none` (no model), `off` (every category switched off), `loading` (an
 * enabled category is still in flight — or not started yet), `failed` (every
 * enabled category failed), `partial` (some failed, the rest are on
 * screen), `ready`.
 */
export type ModelStatus = 'none' | 'off' | 'loading' | 'failed' | 'partial' | 'ready';

export function modelStatus(
  layers: readonly Pick<ModelLayer, 'name' | 'kind'>[],
  switches: Readonly<Record<string, boolean>>,
  plan: boolean,
  loads: Readonly<Record<string, LayerLoad>>,
): ModelStatus {
  if (layers.length === 0) return 'none';
  const on = layers.filter((l) => modelLayerOn(switches, l, plan));
  if (on.length === 0) return 'off';
  let failed = 0;
  for (const l of on) {
    const load = loads[l.name];
    if (load === undefined || load === 'loading') return 'loading';
    if (load === 'error') failed += 1;
  }
  if (failed === on.length) return 'failed';
  return failed > 0 ? 'partial' : 'ready';
}
